import { useNavigate } from 'react-router-dom'

import { Button } from 'components/atoms/button'
import { PHONE_NUMBER_TAKEN_MESSAGE } from 'utils/constants'

interface PhoneTakenSignInPromptProps {
	readonly inviteId?: string
	readonly isModal?: boolean
	/** Sign-up modal only: switch the auth modal to its login view. */
	readonly onSwitchToLogin?: () => void
	readonly checking?: boolean
	/** The availability check confirmed another account has this phone. */
	readonly confirmedTaken?: boolean
	/** Current phone field error, e.g. the "already registered" error from register. */
	readonly phoneError?: string
	readonly getPhoneNumber: () => string | undefined
	/** Login URL with the sign-up page's `?to=`, so the post-login destination survives. */
	readonly loginUrl?: string
}

/**
 * A taken phone on sign-up shouldn't dead-end: tell the user they already have an account and guide them to
 * log in with a code to this phone. The number goes through route state only, never the URL.
 */
export function PhoneTakenSignInPrompt({
	inviteId,
	isModal = false,
	onSwitchToLogin,
	checking = false,
	confirmedTaken = false,
	phoneError,
	getPhoneNumber,
	loginUrl = '/auth/login',
}: PhoneTakenSignInPromptProps) {
	const navigate = useNavigate()
	const isTaken = confirmedTaken || phoneError === PHONE_NUMBER_TAKEN_MESSAGE
	if (checking || !isTaken) return null

	const reason = inviteId ? 'Log in to it to accept the invite.' : 'Log in to it instead.'

	return (
		<div
			className="mt-3 space-y-3 rounded-xl border border-translucent-border bg-primary-surface/[0.4] p-4"
			data-testid="phone-taken-prompt"
		>
			<div>
				<p className="text-primary-text text-sm font-semibold">This phone number already has an account.</p>
				<p className="text-secondary-text text-sm">{reason} No need to sign up again.</p>
			</div>
			{isModal ? (
				onSwitchToLogin && (
					<Button variant="primary" fullWidth type="button" onClick={onSwitchToLogin}>
						Log in instead
					</Button>
				)
			) : (
				<Button
					variant="primary"
					fullWidth
					type="button"
					onClick={() => navigate(loginUrl, { state: { inviteId, phoneNumber: getPhoneNumber() } })}
				>
					Log in with a code to this phone
				</Button>
			)}
		</div>
	)
}
